import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from "react";
import CartServ from "./service/CartServ";
import { message, App } from "antd";
import { useNavigate } from "react-router-dom";
import { LocationContext } from "./LocationContext";
import Notify from "./util/Notify";
import { IconArrowRight } from "@tabler/icons-react";
import { getImageUrl } from "./component/SafeImage";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const { latitude, longitude } = useContext(LocationContext);

  const syncTimeoutRef = useRef(null);

  const saveToLocal = useCallback((newCart) => {
    const token = localStorage.getItem("customerToken") || localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") {
      localStorage.setItem("nearzon_local_cart", JSON.stringify(newCart));
    }
    setCart(newCart);
    const count = newCart?.items?.length || 0;
    setCartCount(count);
  }, []);

  const triggerBackgroundSync = useCallback((currentCartItems, immediate = false) => {
    const token = localStorage.getItem("customerToken") || localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") return;

    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);

    const syncFn = async () => {
      try {
        const response = await CartServ.syncCart(currentCartItems, latitude, longitude);
        if (response) saveToLocal(response);
      } catch (error) {
        console.error("❌ Background sync failed", error);
      }
    };

    if (immediate) {
      syncFn();
    } else {
      syncTimeoutRef.current = setTimeout(syncFn, 3000);
    }
  }, [latitude, longitude, saveToLocal]);

  const updateOptimisticCart = (newItems, immediateSync = false) => {
    let itemTotal = 0;
    let savings = 0;
    newItems.forEach(item => {
      const price = item.price || item.product?.price || 0;
      const mrp = item.mrp || item.product?.mrp || price;
      itemTotal += price * item.quantity;
      savings += (mrp - price) * item.quantity;
    });

    // Fix floating point precision
    itemTotal = Math.round(itemTotal * 100) / 100;
    savings = Math.round(savings * 100) / 100;

    const oldBill = cart.billDetails || {};
    const platformFee = oldBill.platformFee || 0;
    const deliveryFee = oldBill.deliveryFee || 0;
    const handlingFee = oldBill.handlingFee || 0;
    const grandTotal = Math.round((itemTotal + platformFee + deliveryFee + handlingFee) * 100) / 100;

    const newCart = {
      ...cart,
      items: newItems,
      billDetails: {
        ...oldBill,
        itemTotal,
        grandTotal,
        savings,
        toPay: grandTotal,
        totalDiscount: Math.round((savings + (oldBill.couponDiscount || 0) + ((oldBill.deliveryFee && oldBill.freeDelivery) ? oldBill.deliveryFee : 0)) * 100) / 100
      }
    };

    saveToLocal(newCart);
    triggerBackgroundSync(newItems, immediateSync);
  };

  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem("customerToken") || localStorage.getItem("token");
    const localCartStr = localStorage.getItem("nearzon_local_cart");
    let localCart = { items: [] };
    if (localCartStr) {
      try { localCart = JSON.parse(localCartStr); } catch (e) { }
    }

    if (!token || token === "null" || token === "undefined") {
      saveToLocal(localCart);
      return;
    }

    try {
      setLoading(true);
      const data = await CartServ.getCart(latitude, longitude);
      saveToLocal(data || { items: [] });
    } catch (error) {
      saveToLocal(localCart);
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude, saveToLocal]);

  useEffect(() => {
    fetchCart();
    const handleAuthChange = async (e) => {
      const token = localStorage.getItem("customerToken") || localStorage.getItem("token");
      if (token && token !== "null" && token !== "undefined") {
        // Prevent infinite cross-tab ping-pong loops by only merging on explicit login
        if (e && e.type === "auth-change") {
          const localCartStr = localStorage.getItem("nearzon_local_cart");
          if (localCartStr) {
            try {
              const localCart = JSON.parse(localCartStr);
              if (localCart.items && localCart.items.length > 0) {
                const merged = await CartServ.mergeCart(localCart.items, latitude, longitude);
                saveToLocal(merged);
                localStorage.removeItem("nearzon_local_cart"); // FIX: Clear after merge
                return;
              }
            } catch (e) { }
          }
        }
      }
      fetchCart();
    };

    window.addEventListener("storage", handleAuthChange);
    window.addEventListener("auth-change", handleAuthChange);
    return () => {
      window.removeEventListener("storage", handleAuthChange);
      window.removeEventListener("auth-change", handleAuthChange);
    };
  }, [fetchCart, saveToLocal]);

  useEffect(() => {
    const handleShopStatusUpdate = (e) => {
      const data = e.detail;
      setCart(prevCart => {
        if (!prevCart || !prevCart.shopId || !data?.shopId) return prevCart;
        const currentShopId = typeof prevCart.shopId === 'object' ? prevCart.shopId._id : prevCart.shopId;
        if (String(currentShopId) === String(data.shopId)) {
          return { ...prevCart, shopAvailability: data.availability };
        }
        return prevCart;
      });
    };
    window.addEventListener('shopStatusUpdated', handleShopStatusUpdate);
    return () => window.removeEventListener('shopStatusUpdated', handleShopStatusUpdate);
  }, []);

  const addToCart = async (productId, quantity = 1, selectedOptions = {}, productDetails = null, forceClear = false) => {
    const currentShopId = forceClear ? null : (cart.items?.length > 0 ? (cart.items[0].product?.shopId?._id || cart.items[0].product?.shopId) : null);
    const newShopId = productDetails?.shopId || null;

    if (!forceClear && currentShopId && newShopId && currentShopId.toString() !== newShopId.toString()) {
      return new Promise((resolve) => {
        modal.confirm({
          title: "Different Shop Detected",
          centered: true,
          width: 450,
          content: (
            <div className="alert-message text-description">
              <div className="text-bold" style={{ marginBottom: '12px', color: '#374151', fontWeight: '500' }}>
                Your cart contains items from another shop.
              </div>
              <div className="alert-text-muted">
                Clear the cart to add items from this shop.
              </div>
            </div>
          ),
          okText: "Clear Cart",
          cancelText: "Keep Cart",
          okButtonProps: { danger: true },
          onOk: async () => {
            try {
              await addToCart(productId, quantity, selectedOptions, productDetails, true);
              resolve(true);
            } catch (e) { resolve(false); }
          },
          onCancel: () => resolve(false)
        });
      });
    }

    const areOptionsSame = (opts1 = {}, opts2 = {}) => {
      const keys1 = Object.keys(opts1);
      const keys2 = Object.keys(opts2);
      if (keys1.length !== keys2.length) return false;
      return keys1.every(key => opts1[key] === opts2[key]);
    };

    const newItems = forceClear ? [] : [...(cart.items || [])];
    const itemIndex = newItems.findIndex(item =>
      (item.product?._id || item.product) === productId &&
      areOptionsSame(item.selectedOptions, selectedOptions)
    );

    if (itemIndex > -1) {
      newItems[itemIndex].quantity += quantity;
    } else {
      newItems.push({
        _id: "temp_" + Date.now(),
        product: productDetails ? {
          _id: productId,
          name: productDetails.name,
          imageUrl: productDetails.image,
          price: productDetails.price,
          mrp: productDetails.mrp,
          shopId: productDetails.shopId || null
        } : productId,
        price: productDetails?.price,
        mrp: productDetails?.mrp,
        quantity,
        selectedOptions
      });
    }

    updateOptimisticCart(newItems);

    if (productDetails) {
      const { name, image, variant } = productDetails;
      Notify.success(
        "Successfully Added!",
        <div className="pcu-flex pcu-items-center pcu-gap-12" style={{ marginTop: '8px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--color-border-primary)' }}>
            <img src={getImageUrl(image)} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div className="pcu-flex-column">
            <div className="pcu-font-600 pcu-color-title" style={{ fontSize: '13px', lineHeight: '1.2' }}>{name}</div>
            {variant && <div className="pcu-font-12 pcu-color-muted">{variant}</div>}
            <div className="pcu-font-600" style={{ color: 'var(--nearzon-orange)', fontSize: '12px', marginTop: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setIsDrawerOpen(true)}>
              View Cart <IconArrowRight size={14} />
            </div>
          </div>
        </div>
      );
    } else {
      message.success("Item added to cart!");
    }
    return true;
  };

  const updateQuantity = async (itemId, quantity) => {
    const newItems = [...(cart.items || [])];
    const itemIndex = newItems.findIndex(item => item._id === itemId || (item.product?._id || item.product) === itemId);
    if (itemIndex > -1) {
      if (quantity > 0) newItems[itemIndex].quantity = quantity;
      else newItems.splice(itemIndex, 1);
      updateOptimisticCart(newItems);
    }
  };

  const removeFromCart = async (itemId) => {
    const newItems = (cart.items || []).filter(item => item._id !== itemId && (item.product?._id || item.product) !== itemId);
    updateOptimisticCart(newItems, true);
    message.success("Item removed from cart.");
  };

  const clearCart = async () => {
    updateOptimisticCart([]);
    message.success("Cart cleared.");
  };

  const value = React.useMemo(() => ({
    cart, cartCount, loading, addToCart, updateQuantity, removeFromCart, clearCart, refreshCart: fetchCart, isDrawerOpen, setIsDrawerOpen
  }), [cart, cartCount, loading, fetchCart, isDrawerOpen, triggerBackgroundSync, saveToLocal]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
export const useCart = () => useContext(CartContext);
